import { Head as Helmet } from "vite-react-ssg";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  noindex?: boolean;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  keywords?: string;
  publishedTime?: string;
  modifiedTime?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
  geoRegion?: string;
  geoPlacename?: string;
  exactTitle?: boolean;
}

const SITE_NAME = "Prime Realty Partners";
const TWITTER_HANDLE = "@PrimeRealtyNJ";
const DEFAULT_OG_IMAGE = "https://www.prpsj.com/og-image.png";

const SEOHead = ({
  title,
  description,
  canonicalUrl,
  noindex = false,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  schema,
  keywords,
  publishedTime,
  modifiedTime,
  breadcrumbs,
  geoRegion = "US-NJ",
  geoPlacename,
  exactTitle = false,
}: SEOHeadProps) => {
  // Build full title with template (skip suffix when exactTitle is true)
  const fullTitle = exactTitle
    ? title
    : title.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME} - New Jersey Real Estate`;

  // Generate breadcrumb schema if provided
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url,
        })),
      }
    : null;

  // Combine all schemas
  const allSchemas = [];
  if (schema) {
    if (Array.isArray(schema)) {
      allSchemas.push(...schema);
    } else {
      allSchemas.push(schema);
    }
  }
  if (breadcrumbSchema) {
    allSchemas.push(breadcrumbSchema);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="author" content={SITE_NAME} />

      {keywords && <meta name="keywords" content={keywords} />}

      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Geo Meta Tags for Local SEO */}
      <meta name="geo.region" content={geoRegion} />
      {geoPlacename && <meta name="geo.placename" content={geoPlacename} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema.org JSON-LD */}
      {allSchemas.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: (allSchemas.length === 1
              ? JSON.stringify(allSchemas[0])
              : JSON.stringify({ "@context": "https://schema.org", "@graph": allSchemas })
            ).replace(/</g, "\\u003c"),
          }}
        />
      )}
    </Helmet>
  );
};

export default SEOHead;
